import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Home, CheckCircle, Share2, Linkedin, Twitter, Link2, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../App';

const Report = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchConversation();
  }, []);

  const fetchConversation = async () => {
    try {
      const response = await axios.get(`${API}/conversation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.phase_data?.completion_report) {
        setConversation(response.data);
      } else {
        toast.error('No report available');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Could not load report');
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/conversation/report/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SPRING_Stage0_Report_${user?.name?.replace(/\s+/g, '_') || 'User'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Could not download report');
    }
    setDownloading(false);
  };

  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareText = `I just completed Stage 0 of SPRING — a program helping New Zealanders transition into self-employment. Feeling excited about my next chapter! 🌱`;
  const shareUrl = window.location.origin;

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
    toast.success('Opening LinkedIn...');
  };

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
    toast.success('Opening X...');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse">
          <span className="text-xl headline text-black">Loading...</span>
        </div>
      </div>
    );
  }

  const report = conversation?.phase_data?.completion_report;
  if (!report) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-black/60">No report available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-white z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-black/60 hover:text-black"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </button>
          <h1 className="headline text-black">SPRING</h1>
          <div className="w-16" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Title */}
        <div className="text-center mb-10 animate-fade-in">
          <div className="w-16 h-16 bg-primary rounded-none flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-black" />
          </div>
          <h1 className="text-3xl headline text-black mb-2">Stage 0 Complete</h1>
          <p className="text-black/60 body-text">
            Your Completion Report
          </p>
        </div>

        {/* Report Content */}
        <div className="space-y-8 animate-slide-up">
          {/* Selected Ideas */}
          <section className="bg-secondary rounded-none p-6">
            <h2 className="text-xl subhead text-black mb-4">Your Selected Ideas</h2>
            <div className="space-y-3">
              {report.selected_ideas?.map((idea, index) => (
                <div key={index} className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-primary rounded-none flex items-center justify-center flex-shrink-0 text-sm headline text-black">
                    {index + 1}
                  </span>
                  <p className="body-text text-black">{idea}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Why It Suits You */}
          <section>
            <h2 className="text-xl subhead text-black mb-4">Why These Suit You</h2>
            <p className="body-text text-black/80 leading-relaxed">
              {report.why_it_suits}
            </p>
          </section>

          {/* Key Unknowns */}
          <section>
            <h2 className="text-xl subhead text-black mb-4">Key Unknowns to Explore</h2>
            <ul className="space-y-2">
              {report.unknowns?.map((unknown, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-primary">•</span>
                  <span className="body-text text-black/80">{unknown}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Next Steps */}
          <section className="bg-primary/10 rounded-none p-6">
            <h2 className="text-xl subhead text-black mb-4">Your First 3 Steps</h2>
            <div className="space-y-4">
              {report.next_steps?.map((step, index) => (
                <div key={index} className="flex items-start gap-4">
                  <span className="w-8 h-8 bg-primary rounded-none flex items-center justify-center flex-shrink-0 headline text-black">
                    {index + 1}
                  </span>
                  <p className="body-text text-black pt-1">{step}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Actions */}
        <div className="mt-10 space-y-4">
          <Button
            onClick={downloadPDF}
            disabled={downloading}
            className="w-full bg-primary text-white hover:bg-primary/90 rounded-none h-14 text-base subhead btn-spring"
            data-testid="download-pdf-btn"
          >
            <Download className="mr-2 h-5 w-5" />
            {downloading ? 'Downloading...' : 'Download PDF Report'}
          </Button>
          
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="w-full rounded-none h-14 text-base border-border"
            data-testid="back-to-dashboard-btn"
          >
            <Home className="mr-2 h-5 w-5" />
            Back to Dashboard
          </Button>
        </div>

        {/* Share Your Journey */}
        <div className="mt-10 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl p-8 text-center animate-fade-in">
          <div className="w-14 h-14 bg-primary rounded-none flex items-center justify-center mx-auto mb-4">
            <PartyPopper className="h-7 w-7 text-black" />
          </div>
          <h2 className="text-2xl headline text-black mb-2">Share Your Journey</h2>
          <p className="text-black/60 body-text mb-6 max-w-md mx-auto">
            You've taken a bold step. Celebrate it! Share with your network and inspire others.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={shareToLinkedIn}
              className="w-full sm:w-auto bg-[#0A66C2] text-white hover:bg-[#0A66C2]/90 rounded-none px-6 h-12 subhead btn-spring"
              data-testid="share-linkedin-btn"
            >
              <Linkedin className="mr-2 h-5 w-5" />
              LinkedIn
            </Button>
            
            <Button
              onClick={shareToTwitter}
              className="w-full sm:w-auto bg-black text-white hover:bg-black/90 rounded-none px-6 h-12 subhead btn-spring"
              data-testid="share-twitter-btn"
            >
              <Twitter className="mr-2 h-5 w-5" />
              X / Twitter
            </Button>
            
            <Button
              onClick={copyLink}
              variant="outline"
              className="w-full sm:w-auto rounded-none px-6 h-12 border-border subhead"
              data-testid="copy-link-btn"
            >
              <Link2 className="mr-2 h-5 w-5" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          
          <p className="text-sm text-black/40 mt-6">
            Every share plants a seed of possibility
          </p>
        </div>

        {/* Footer Message */}
        <div className="mt-10 text-center py-8 border-t border-border">
          <p className="body-text text-black/60 mb-2">
            This is YOUR document — the foundation for Stage 1.
          </p>
          <p className="text-sm text-black/40">
            SPRING • A way up. Not alone.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Report;
